import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import FormInput from '../../../components/common/FormInput';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../../../services/api';

const validationSchema = Yup.object().shape({
  cluster: Yup.object().shape({
    id: Yup.number().nullable(),
    name: Yup.string().when(['id'], {
      is: (id) => id === null,
      then: () => Yup.string().required('Cluster name is required when creating new cluster'),
      otherwise: () => Yup.string().notRequired()
    })
  }),
  fcp: Yup.object().shape({
    id: Yup.number().nullable(),
    name: Yup.string().when(['id'], {
      is: (id) => id === null,
      then: () => Yup.string().required('FCP name is required when creating new FCP'),
      otherwise: () => Yup.string().notRequired()
    })
  }),
  participantNumber: Yup.string()
    .required('Participant number is required')
    .matches(
      /^[A-Za-z0-9]{3,20}$/,
      'Must be 3–20 alphanumeric characters (e.g. KE123456789, ABC001, or a 3-digit short ID like 789)'
    )
});

const ParticipantRegistrationForm = ({ onSuccess, onCancel }) => {
  const [clusters, setClusters] = useState([]);
  const [allFcps, setAllFcps] = useState([]);
  const [filteredFcps, setFilteredFcps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formik = useFormik({
    initialValues: {
      cluster: { id: null, name: '' },
      fcp: { id: null, name: '' },
      participantNumber: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post('/admin/participant-registration', {
          clusterData: values.cluster,
          fcpData: values.fcp,
          participantData: {
            participantNumber: values.participantNumber
          }
        });

        if (response.data.success) {
          onSuccess?.(response.data.data);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'An error occurred during registration');
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clustersResponse, fcpsResponse] = await Promise.all([
          api.get('/admin/clusters'),
          api.get('/admin/fcps')
        ]);

        if (clustersResponse.data.success) {
          setClusters(clustersResponse.data.data);
        }

        if (fcpsResponse.data.success) {
          setAllFcps(fcpsResponse.data.data);
          setFilteredFcps(fcpsResponse.data.data);
        }
      } catch (err) {
        setError('Failed to load existing data');
      }
    };

    fetchData();
  }, []);

  const handleClusterSelect = (value) => {
    const clusterId = parseInt(value);
    if (clusterId) {
      formik.setFieldValue('cluster', { id: clusterId, name: '' });
      // Filter FCPs based on selected cluster
      const filtered = allFcps.filter(fcp => fcp.clusterId === clusterId);
      setFilteredFcps(filtered);
      // Reset FCP selection since cluster changed
      formik.setFieldValue('fcp', { id: null, name: '' });
    } else {
      formik.setFieldValue('cluster', { id: null, name: '' });
      // Show all FCPs when no cluster is selected
      setFilteredFcps(allFcps);
      formik.setFieldValue('fcp', { id: null, name: '' });
    }
  };

  const handleFCPSelect = (value) => {
    const fcpId = parseInt(value);
    if (fcpId) {
      formik.setFieldValue('fcp', { id: fcpId, name: '' });
    } else {
      formik.setFieldValue('fcp', { id: null, name: '' });
    }
  };

  return (
    <View style={styles.container}>
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <View style={styles.formSection}>
        {/* Cluster Selection/Creation */}
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Cluster</Text>
          <View style={styles.pickerContainer}>
            <Picker
              selectedValue={formik.values.cluster.id?.toString() || ''}
              onValueChange={handleClusterSelect}
              style={styles.picker}
            >
              <Picker.Item label="Select existing or create new" value="" />
              {clusters.map(cluster => (
                <Picker.Item
                  key={cluster.id}
                  label={cluster.name}
                  value={cluster.id.toString()}
                />
              ))}
              <Picker.Item label="+ Create New Cluster" value="" />
            </Picker>
          </View>

          {!formik.values.cluster.id && (
            <FormInput
              label="New Cluster Name"
              name="cluster.name"
              value={formik.values.cluster.name}
              onChangeText={(text) => formik.setFieldValue('cluster.name', text)}
              error={formik.touched.cluster?.name && formik.errors.cluster?.name}
              containerStyle={styles.textInput}
            />
          )}
        </View>

        {/* FCP Selection/Creation */}
        <View style={styles.inputGroup}>
          <Text style={styles.label}>FCP</Text>
          <View style={styles.pickerContainer}>
            <Picker
              selectedValue={formik.values.fcp.id?.toString() || ''}
              onValueChange={handleFCPSelect}
              style={styles.picker}
            >
              <Picker.Item label="Select existing or create new" value="" />
              {filteredFcps.map(fcp => (
                <Picker.Item
                  key={fcp.id}
                  label={fcp.name}
                  value={fcp.id.toString()}
                />
              ))}
              <Picker.Item label="+ Create New FCP" value="" />
            </Picker>
          </View>

          {!formik.values.fcp.id && (
            <FormInput
              label="New FCP Name"
              name="fcp.name"
              value={formik.values.fcp.name}
              onChangeText={(text) => formik.setFieldValue('fcp.name', text)}
              error={formik.touched.fcp?.name && formik.errors.fcp?.name}
              containerStyle={styles.textInput}
            />
          )}
        </View>

        {/* Participant Details */}
        <FormInput
          label="Participant Number"
          name="participantNumber"
          value={formik.values.participantNumber}
          onChangeText={(text) => formik.setFieldValue('participantNumber', text.trim())}
          error={formik.touched.participantNumber && formik.errors.participantNumber}
          placeholder="e.g. KE123456789, ABC001 or 789"
          autoCapitalize="characters"
          containerStyle={styles.textInput}
        />
      </View>

      <View style={styles.buttonContainer}>
        <TouchableOpacity
          onPress={onCancel}
          style={styles.cancelButton}
        >
          <Text style={styles.cancelButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={formik.handleSubmit}
          disabled={loading}
          style={[styles.submitButton, loading && styles.submitButtonDisabled]}
        >
          <Text style={styles.submitButtonText}>
            {loading ? 'Registering...' : 'Register Participant'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#F87171',
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    color: '#B91C1C',
    fontSize: 14,
  },
  formSection: {
    gap: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter' } : {}),
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    marginBottom: 8,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
    }),
  },
  picker: {
    height: 44,
    ...(Platform.OS === 'web' ? {
      outlineStyle: 'none',
      padding: '8px 12px',
    } : {}),
  },
  textInput: {
    marginTop: 8,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 24,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
  },
  cancelButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter' } : {}),
  },
  submitButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: '#2563EB',
    ...(Platform.OS === 'web' ? {
      cursor: 'pointer',
    } : {}),
  },
  submitButtonDisabled: {
    opacity: 0.5,
    ...(Platform.OS === 'web' ? {
      cursor: 'not-allowed',
    } : {}),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter' } : {}),
  },
});

export default ParticipantRegistrationForm;
